import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, User, Calendar, Eye, Trash } from '@phosphor-icons/react';
import GlobalHeader from '../GlobalHeader';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CmsCompetitionEntries() {
  const { competitionId } = useParams();
  const navigate = useNavigate();
  const [competition, setCompetition] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [competitionId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [compRes, entriesRes] = await Promise.all([
        axios.get(`${API}/api/admin/competitions/${competitionId}`, { withCredentials: true }),
        axios.get(`${API}/api/admin/competitions/${competitionId}/entries`, { withCredentials: true })
      ]);
      
      setCompetition(compRes.data);
      setEntries(entriesRes.data.entries || entriesRes.data || []);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Failed to load entries');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Delete this entry?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/competitions/${competitionId}/entries/${entryId}`, {
        withCredentials: true
      });
      toast.success('Entry deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <GlobalHeader />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />
      <div className="container mx-auto px-6 py-12 max-w-6xl">
        <div className="space-y-6">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft size={16} />
            Back to Admin Dashboard
          </button>

          <div>
            <h1 className="text-3xl font-bold mb-2">Competition Entries</h1>
            {competition && (
              <p className="text-muted-foreground">{competition.title}</p>
            )}
          </div>

          {entries.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <p className="text-muted-foreground">No entries submitted yet</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {entries.map((entry) => (
                <div
                  key={entry.entry_id}
                  className="bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-2">{entry.title}</h3>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <User size={16} />
                          {entry.author_name || entry.user_name || 'Anonymous'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar size={16} />
                          {new Date(entry.submitted_at || entry.created_at).toLocaleDateString()}
                        </span>
                        {entry.votes !== undefined && (
                          <span>👍 {entry.votes} votes</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => window.open(`/article/${entry.article_id}`, '_blank')}
                        className="p-2 hover:bg-muted rounded-lg transition-colors"
                        title="View"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(entry.entry_id)}
                        className="p-2 hover:bg-destructive/10 text-destructive rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                  </div>
                  
                  {entry.content && (
                    <p className="text-sm text-muted-foreground line-clamp-3">
                      {entry.content.substring(0, 200)}...
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
